import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import {
  Activity, ArrowLeft, ArrowRight, Baby, Bike, Check, CircleDot, Dumbbell, Flower2, Hand, HeartPulse,
  Loader2, Music, Pencil, Phone, Plus, Timer, Trophy, Users, Waves, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import PhotoSlot from './PhotoSlots';
import { PHOTO_ASSETS } from '../data/photoAssets';
import { submitLead, uncertainInquiryMessage } from '../lib/submitLead';
import { fieldMessage } from '../lib/formRules';
import './InquiryJourney.css';

// Every request form on the site is a short journey modelled on Book a Tour (owner direction,
// Sept 30): a welcome, one easy question per screen, a plan card that fills in as the visitor
// goes, a review with Edit buttons, and a clear confirmation. Each form still sends exactly the
// inquiry it sent before; `tone` only sets the accent to the program's color.

export const HOW_DID_YOU_HEAR_OPTIONS = [
  'Instagram', 'Facebook', 'TikTok', 'Google Search', 'Yelp', 'Nextdoor',
  'Referred by a friend or family member', 'Current member referral', 'Community event', 'Other',
];

// The interests every inquiry form offers, grouped so they're easy to scan: the original 16 plus
// Pickleball (owner direction, Oct 6), which is played on the basketball court and previews it.
// Each has an icon and the photo space that previews it.
export const INTEREST_GROUPS = [
  ['Train', [
    ['Personal Training', Dumbbell, 'P03'], ['Small Group Training', Users, 'P02'],
    ['Functional Training', Zap, 'A03'], ['Machine Weights', Dumbbell, 'H05'], ['Cardio Equipment', HeartPulse, 'schedule-F01-Cardio%20floor%20and%20treadmills'],
  ]],
  ['Classes', [
    ['Group Fitness', Activity, 'C03'], ['Group Cycling', Bike, 'C02'], ['Cardio Workouts', Timer, 'C02'],
    ['Zumba', Music, 'journey-zumba'], ['Mind Body', Flower2, 'schedule-mind-body'], ['Reformer Pilates', Flower2, 'P01'],
  ]],
  ['Pool & family', [['Lap Pools', Waves, 'A02'], ['Swim Lessons', Waves, 'swim-hero'], ["Kid's Club", Baby, 'C05']]],
  ['Play & recover', [['Basketball', Trophy, 'A01'], ['Pickleball', CircleDot, 'A01'], ['Therapeutic Massage', Hand, null]]],
];
export const INTEREST_PHOTO = Object.fromEntries(INTEREST_GROUPS.flatMap(([, items]) => items.map(([name, , slot]) => [name, slot])));
const hasPhoto = (slot) => Boolean(slot && PHOTO_ASSETS[slot]);
// The photo panel is half the screen wide but the full screen tall, so a landscape photo shows wider
// than the panel: ask for about three quarters of the screen width to keep it sharp.
const PANEL_SIZES = '(max-width: 960px) 100vw, 75vw';

// How far the top of a heading's capital letters sits below the top of its box, so the plan card can
// line up with the letters themselves rather than the space above them.
const capInset = (heading) => {
  const style = getComputedStyle(heading);
  const pen = document.createElement('canvas').getContext('2d');
  if (!pen) return 0;
  pen.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  const { fontBoundingBoxAscent: ascent, fontBoundingBoxDescent: descent, actualBoundingBoxAscent: cap } = pen.measureText('T');
  return (parseFloat(style.lineHeight) - ascent - descent) / 2 + ascent - cap;
};

/*
 * steps: [{ label, question(ctx), lede, render(ctx), photo?(ctx), caption?(ctx), nextLabel?(ctx) }]
 *   The welcome screen comes first; the last step is the review (plan rows with Edit buttons,
 *   then its own fields), whose button sends the inquiry.
 * plan: { title, rows(ctx) → [[label, value, editStep?]] } — the card beside every step.
 * success: { title, heading(ctx), lede, next: [[Icon, strong, text]], actions, again }
 * guided: no welcome screen (owner direction, Sept 30). The first question shows straight away under
 *   the form's name (welcome.eyebrow, at welcomeLevel), a one-line breakdown (welcome.lede) and
 *   "Step 1 of N".
 */
export default function InquiryJourney({
  id, tone = 'blue', testId = 'inquiry-journey', welcomeLevel = 'h2', progressLabel = 'Request steps',
  welcome, steps, initial, payload, missing, plan, startLabel, submitLabel, success, guided = false,
}) {
  const formId = useId();
  const pending = useRef(false);
  const headingRef = useRef(null);
  const stepRef = useRef(null);
  const feedbackRef = useRef(null);
  const successRef = useRef(null);
  const moved = useRef(false);
  const firstStep = guided ? 1 : 0;
  const [step, setStep] = useState(firstStep);
  const [preview, setPreview] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [form, setForm] = useState(initial);

  // Guided forms start the plan card level with the top of the question beside it (owner direction,
  // Sept 30), following it from screen to screen.
  const [planTop, setPlanTop] = useState(null);
  useLayoutEffect(() => {
    const panel = stepRef.current;
    if (!guided || !panel || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const heading = panel.querySelector('.dv-journey__question');
      if (!heading) return;
      let top = capInset(heading);
      for (let node = heading; node && node !== panel; node = node.offsetParent) top += node.offsetTop;
      setPlanTop(Math.round(top));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [guided, step, isSubmitted]);

  const fieldId = (name) => `${formId}-${name}`;
  const titleId = `${formId}-title`;
  const go = (next) => { moved.current = true; setFeedback(null); setPreview(null); setStep(next); };
  const ctx = {
    form, preview, setPreview, go, fieldId, titleId,
    firstName: (form.firstName ?? form.name ?? form.hostName ?? '').trim().split(/\s+/)[0],
    set: (field, value) => setForm((prev) => ({ ...prev, [field]: value })),
    update: (field) => (event) => { const { value } = event.target; setForm((prev) => ({ ...prev, [field]: value })); },
    toggle: (field, value) => setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value) ? prev[field].filter((item) => item !== value) : [...prev[field], value],
    })),
    invalid: (name) => feedback?.fields.some((field) => field.id === fieldId(name)) || undefined,
  };

  // Move focus to each new step's question so keyboard and screen-reader users follow along.
  useEffect(() => {
    if (!moved.current) return;
    headingRef.current?.focus({ preventScroll: true });
    stepRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [step]);
  useEffect(() => {
    if (!feedback) return;
    feedbackRef.current?.focus({ preventScroll: true });
    feedbackRef.current?.scrollIntoView?.({ behavior: 'instant', block: 'center' });
  }, [feedback]);
  useEffect(() => {
    if (!isSubmitted) return;
    successRef.current?.focus({ preventScroll: true });
    successRef.current?.scrollIntoView?.({ behavior: 'instant', block: 'start' });
  }, [isSubmitted]);

  const checkStep = () => {
    const fields = [...(stepRef.current?.querySelectorAll('input, select, textarea') || [])].filter((field) => field.willValidate && !field.validity.valid);
    if (!fields.length) return true;
    setFeedback({
      message: 'Please check the highlighted details.',
      fields: fields.map((field) => ({ id: field.id, message: fieldMessage(field) })),
    });
    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (pending.current || isSubmitted) return;
    if (step < steps.length) {
      if (checkStep()) go(step + 1);
      return;
    }
    if (!checkStep()) return;
    const back = missing?.(form) ?? -1;
    if (back > 0) { go(back); return; }
    setFeedback(null);
    pending.current = true;
    setIsSubmitting(true);
    try {
      const { response, data } = await submitLead(payload(form));
      if (response.ok && data.success) {
        setIsSubmitted(true);
        toast.success(data.message || success.title);
      } else {
        const message = data.detail || 'Something went wrong. Please try again.';
        setFeedback({ message, fields: [] });
        toast.error(message);
      }
    } catch {
      setFeedback({ message: uncertainInquiryMessage, fields: [] });
      toast.error(uncertainInquiryMessage);
    } finally {
      pending.current = false;
      setIsSubmitting(false);
    }
  };

  const startOver = () => { moved.current = true; setForm(initial); setIsSubmitted(false); setFeedback(null); setStep(firstStep); };

  const current = step > 0 ? steps[step - 1] : null;
  const photo = isSubmitted || !current ? (success.photo?.(ctx) ?? welcome.photo) : (current.photo?.(ctx) ?? welcome.photo);
  const caption = !isSubmitted && current?.caption?.(ctx);
  const rows = plan.rows(ctx);
  const onReview = !isSubmitted && step === steps.length;
  const Welcome = welcomeLevel;

  const card = (
    <aside className={`dv-journey__pass${isSubmitted ? ' dv-journey__pass--done' : ''}`} aria-label={plan.title}>
      <p className="dv-journey__pass-title">{plan.title}</p>
      <dl>{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || '—'}</dd></div>)}</dl>
      {isSubmitted && <span className="dv-journey__stamp">Requested</span>}
    </aside>
  );

  return (
    <section id={id} className={`dv-journey dv-journey--${tone}${guided ? ' dv-journey--guided' : ''}`} aria-labelledby={titleId} data-testid={testId}>
      <div className={`dv-journey__media${hasPhoto(photo) ? '' : ' dv-journey__media--field'}`} aria-hidden={caption ? undefined : 'true'}>
        {hasPhoto(photo) && <PhotoSlot key={photo} id={photo} label="Delta Valley Health Club" className="dv-journey__photo" sizes={PANEL_SIZES} />}
        <span className="dv-journey__haze" aria-hidden="true" />
        {caption && <p className="dv-journey__caption" aria-live="polite">Preview · {caption}</p>}
        {!onReview && <div className="dv-journey__pass-wrap" style={planTop === null ? undefined : { '--journey-plan-top': `${planTop}px` }}>{card}</div>}
      </div>

      <div className="dv-journey__panel" ref={stepRef}>
        {guided && <Welcome className="dv-journey__eyebrow dv-journey__form-name">{welcome.eyebrow}</Welcome>}
        {isSubmitted ? (
          <div ref={successRef} tabIndex={-1} role="status" aria-label={success.title} className="dv-journey__success">
            <p className="dv-journey__eyebrow"><Check size={16} aria-hidden="true" /> {success.title}</p>
            <h2 className="dv-size-heading dv-journey__question" id={titleId}><span>{success.heading(ctx)}</span></h2>
            <p className="dv-size-lede dv-journey__lede">{success.lede}</p>
            <ol className="dv-journey__next">
              {success.next.map(([Icon, strong, text]) => <li key={strong}><Icon aria-hidden="true" /><span><strong>{strong}</strong>{typeof text === 'function' ? text(ctx) : text}</span></li>)}
            </ol>
            <div className="dv-journey__actions">
              {success.actions}
              {success.again && <button type="button" className="dv-journey__back" onClick={startOver}>{success.again}</button>}
            </div>
            <p className="dv-journey__call">Questions? Call <a href="tel:9252402990">(925) 240-2990</a></p>
            <div className="dv-journey__pass-mobile">{card}</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate aria-busy={isSubmitting} className="dv-journey__form">
            {guided && step === firstStep && welcome.lede && <p className="dv-size-body dv-journey__breakdown">{welcome.lede}</p>}
            {guided && <p className="dv-journey__count">Step {step} of {steps.length}</p>}
            {step > 0 && (
              <ol className={`dv-journey__progress${steps.length > 4 ? ' dv-journey__progress--long' : ''}`} aria-label={progressLabel} style={{ '--journey-steps': steps.length }}>
                {steps.map(({ label }, index) => (
                  <li key={label} aria-current={index + 1 === step ? 'step' : undefined} className={index + 1 < step ? 'is-done' : undefined}>
                    <span>{label}</span>
                  </li>
                ))}
              </ol>
            )}

            {feedback && (
              <div ref={feedbackRef} tabIndex={-1} role="alert" data-error="true" className="dv-journey__feedback">
                <p className="dv-size-body">{feedback.message}</p>
                {feedback.fields.length > 0 && <ul>{feedback.fields.map((field) => <li key={field.id}><a href={`#${field.id}`} onClick={(event) => { event.preventDefault(); document.getElementById(field.id)?.focus(); }}>{field.message}</a></li>)}</ul>}
              </div>
            )}

            <div className="dv-journey__step" key={step}>
              {current ? (
                <>
                  <h2 ref={headingRef} tabIndex={-1} className="dv-size-heading dv-journey__question" id={titleId}>
                    <span>{current.question(ctx)}</span>
                  </h2>
                  {current.lede && <p className="dv-size-lede dv-journey__lede">{typeof current.lede === 'function' ? current.lede(ctx) : current.lede}</p>}
                  {onReview && (
                    <dl className="dv-journey__review">
                      {rows.map(([label, value, edit]) => <ReviewRow key={label} label={label} value={value} onEdit={edit ? () => go(edit) : undefined} />)}
                    </dl>
                  )}
                  {current.render(ctx)}
                </>
              ) : (
                <>
                  <p className="dv-journey__eyebrow">{welcome.eyebrow}</p>
                  <Welcome ref={headingRef} tabIndex={-1} className="dv-size-heading dv-journey__question" id={titleId}>
                    <span>{welcome.title[0]}</span>
                    {welcome.title[1] && <em>{welcome.title[1]}</em>}
                  </Welcome>
                  <p className="dv-size-lede dv-journey__lede">{welcome.lede}</p>
                  <ul className="dv-journey__promises">
                    {welcome.promises.map(([Icon, text]) => <li key={text}><Icon aria-hidden="true" /> {text}</li>)}
                  </ul>
                  {welcome.note && <p className="dv-size-body dv-journey__note">{welcome.note}</p>}
                </>
              )}
            </div>

            <div className="dv-journey__nav">
              {step > firstStep && (
                <button type="button" className="dv-journey__back" onClick={() => go(step - 1)}>
                  <ArrowLeft size={16} aria-hidden="true" /> Back
                </button>
              )}
              <button type="submit" className="dv-button dv-button--light dv-journey__next-button" disabled={isSubmitting}>
                {isSubmitting ? <><Loader2 className="animate-spin" size={16} aria-hidden="true" /> Sending…</>
                  : step === 0 ? <>{startLabel} <ArrowRight size={16} aria-hidden="true" /></>
                    : onReview ? <>{submitLabel} <Check size={16} aria-hidden="true" /></>
                      : <>{current.nextLabel?.(ctx) || 'Continue'} <ArrowRight size={16} aria-hidden="true" /></>}
              </button>
            </div>
            <p className="dv-journey__call">Prefer to talk? <a href="tel:9252402990"><Phone size={14} aria-hidden="true" /> (925) 240-2990</a></p>
            {onReview && <div className="dv-journey__pass-mobile">{card}</div>}
          </form>
        )}
      </div>
    </section>
  );
}

export function Field({ id, label, icon: Icon, hint, invalid, input, multiline }) {
  const value = input.value ?? '';
  const filled = Boolean(String(value).trim()) && (input.type !== 'email' || /.+@.+\..+/.test(value));
  return (
    <div className={`dv-journey__field${filled ? ' is-filled' : ''}`}>
      <label htmlFor={id}>{label}</label>
      {multiline ? <textarea id={id} aria-invalid={invalid} {...input} /> : (
        <div className="dv-journey__input">
          {Icon && <Icon className="dv-journey__input-icon" aria-hidden="true" />}
          <input id={id} aria-invalid={invalid} className={Icon ? 'has-icon' : undefined} {...input} />
          <Check className="dv-journey__input-ok" aria-hidden="true" />
        </div>
      )}
      {hint && <p className="dv-journey__hint">{hint}</p>}
    </div>
  );
}

export function Tile({ on, onClick, icon: Icon, label, detail, price, large, onPreview }) {
  return (
    <button type="button" aria-pressed={on} className={`dv-journey__tile${large ? ' dv-journey__tile--large' : ''}`} onClick={onClick}
      onMouseEnter={onPreview && (() => onPreview(label))} onMouseLeave={onPreview && (() => onPreview(null))}
      onFocus={onPreview && (() => onPreview(label))} onBlur={onPreview && (() => onPreview(null))}>
      {Icon && <span className="dv-journey__tile-icon"><Icon aria-hidden="true" /></span>}
      <span className="dv-journey__tile-label">
        {label}{price !== undefined && <> <span className="dv-journey__tile-price">${price}</span></>}
        {detail && <small>{detail}</small>}
      </span>
      <span className="dv-journey__tile-check"><Check aria-hidden="true" /></span>
    </button>
  );
}

// The 16 interest tiles, previewing each space in the photo panel on hover or focus.
export function InterestTiles({ ctx, labelledBy }) {
  return (
    <div className="dv-journey__groups" role="group" aria-labelledby={labelledBy}>
      {INTEREST_GROUPS.map(([group, items]) => (
        <div key={group} className="dv-journey__group">
          <p className="dv-journey__group-label">{group}</p>
          <div className="dv-journey__tiles">
            {items.map(([interest, Icon]) => (
              <Tile key={interest} label={interest} icon={Icon} on={ctx.form.interests.includes(interest)}
                onClick={() => ctx.toggle('interests', interest)} onPreview={ctx.setPreview} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// One interest group per screen (guided forms), ending with "Other", which opens a short text box.
// ctx.form.others holds the typed answer for each group that has "Other" chosen.
export const OTHER_MAX = 75;
export function GroupTiles({ ctx, group, items, labelledBy }) {
  const other = ctx.form.others[group];
  const otherOn = other !== undefined;
  const setOther = (value) => {
    const others = { ...ctx.form.others };
    if (value === undefined) delete others[group]; else others[group] = value;
    ctx.set('others', others);
  };
  return (
    <div className="dv-journey__groups">
      <div className="dv-journey__tiles dv-journey__tiles--guided" role="group" aria-labelledby={labelledBy}>
        {items.map(([interest, Icon]) => (
          <Tile key={interest} label={interest} icon={Icon} on={ctx.form.interests.includes(interest)}
            onClick={() => ctx.toggle('interests', interest)} onPreview={ctx.setPreview} />
        ))}
        <Tile label="Other" icon={Plus} on={otherOn} onClick={() => setOther(otherOn ? undefined : '')} />
      </div>
      {otherOn && (
        <Field id={ctx.fieldId(`other-${group}`)} label="What else would you like to see? (optional)"
          input={{ type: 'text', placeholder: 'Tell us in a few words', maxLength: OTHER_MAX, autoFocus: true, value: other, onChange: (event) => setOther(event.target.value) }} />
      )}
    </div>
  );
}

export function HowDidYouHear({ ctx }) {
  return (
    <div className="dv-journey__field">
      <label htmlFor={ctx.fieldId('referral')}>How did you hear about us? *</label>
      <select id={ctx.fieldId('referral')} required aria-invalid={ctx.invalid('referral')} data-message="Please choose an answer."
        value={ctx.form.howDidYouHear} onChange={ctx.update('howDidYouHear')} data-testid="lead-how-did-you-hear">
        <option value="">Please select</option>
        {HOW_DID_YOU_HEAR_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </div>
  );
}

export function Privacy({ children }) {
  return <p className="dv-journey__privacy">{children || 'By sending, you agree that Delta Valley Health Club may contact you about this request. We respect your privacy and won’t share your information.'}</p>;
}

function ReviewRow({ label, value, onEdit }) {
  return (
    <div className="dv-journey__review-row">
      <dt>{label}</dt>
      <dd>
        <span>{value || '—'}</span>
        {onEdit && <button type="button" className="dv-journey__edit" onClick={onEdit}><Pencil size={14} aria-hidden="true" /> Edit<span className="sr-only"> {label}</span></button>}
      </dd>
    </div>
  );
}
